import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import SocialIcons from './SocialIcons';
import { BiMenu, BiX } from "react-icons/bi";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from '../contexts/ThemeContext';
import BrandLogo from './BrandLogo';
// import ThemeToggle from './ThemeToggle';

const Navbar = () => {
    const location = useLocation();
    const [selectedNav, setSelectedNav] = React.useState('/');
    const [hamburgerOpened, setHamburgerOpened] = React.useState(false);
    const [scrolled, setScrolled] = React.useState(false);
    const { isDark } = useTheme();

    React.useEffect(() => {
        if (location.pathname) {
            setSelectedNav(location.pathname.split('/')[1]);
        }
    }, [location])

    // Close hamburger menu when route changes
    React.useEffect(() => {
        setHamburgerOpened(false);
    }, [location.pathname])

    // Track scroll position
    React.useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 100;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [scrolled])

    const showHamburgerMenu = () => {
        return (
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className={`block absolute right-0 top-12 rounded-lg shadow-lg z-50 overflow-hidden border ${
                        isDark
                            ? 'bg-dark-bg-card border-dark-border'
                            : 'bg-light-bg-card border-light-border shadow-xl'
                    }`}
                >
                    <Link to="/">
                        <li className={`py-3 px-4 whitespace-nowrap transition-colors ${
                            selectedNav === ''
                                ? (isDark ? 'text-dark-brand-primary bg-dark-bg-tertiary' : 'text-light-brand-primary bg-light-bg-tertiary')
                                : (isDark ? 'text-dark-text-primary hover:bg-dark-bg-tertiary' : 'text-light-text-primary hover:bg-light-bg-tertiary')
                        }`}>
                            Home
                        </li>
                    </Link>
                    <Link to="/about">
                        <li className={`py-3 px-4 whitespace-nowrap transition-colors ${
                            selectedNav === 'about'
                                ? (isDark ? 'text-dark-brand-primary bg-dark-bg-tertiary' : 'text-light-brand-primary bg-light-bg-tertiary')
                                : (isDark ? 'text-dark-text-primary hover:bg-dark-bg-tertiary' : 'text-light-text-primary hover:bg-light-bg-tertiary')
                        }`}>
                            About
                        </li>
                    </Link>
                    <Link to={{pathname: "/projects", state: { tab: "All"}}}>
                        <li className={`py-3 px-4 whitespace-nowrap transition-colors ${
                            selectedNav === 'projects'
                                ? (isDark ? 'text-dark-brand-primary bg-dark-bg-tertiary' : 'text-light-brand-primary bg-light-bg-tertiary')
                                : (isDark ? 'text-dark-text-primary hover:bg-dark-bg-tertiary' : 'text-light-text-primary hover:bg-light-bg-tertiary')
                        }`}>
                            Projects
                        </li>
                    </Link>
                    <Link to="/hireme">
                        <li className={`py-3 px-4 whitespace-nowrap transition-colors ${
                            selectedNav === 'hireme'
                                ? (isDark ? 'text-dark-brand-primary bg-dark-bg-tertiary' : 'text-light-brand-primary bg-light-bg-tertiary')
                                : (isDark ? 'text-dark-text-primary hover:bg-dark-bg-tertiary' : 'text-light-text-primary hover:bg-light-bg-tertiary')
                        }`}>
                            Contact Me
                        </li>
                    </Link>
                </motion.div>
            </AnimatePresence>
        )
    }

    return (
        <nav className={`sticky top-0 z-50 flex flex-wrap md:flex-nowrap justify-between items-center transition-all duration-300 ${
            scrolled ? 'py-3 md:py-4 shadow-lg' : 'py-6 md:py-8'
        } ${isDark ? 'bg-dark-bg-primary' : 'bg-light-bg-primary'}`}>
            {/* Brand Logo */}
            <Link to="/">
                <BrandLogo variant="command" />
            </Link>

            {/* Mobile: Theme Toggle + Hamburger */}
            <div className='md:hidden flex items-center gap-3'>
                {/* <ThemeToggle /> */}
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`relative p-2 rounded-lg transition-colors ${
                        isDark
                            ? 'text-dark-text-primary hover:bg-dark-bg-secondary'
                            : 'text-light-text-primary hover:bg-light-bg-secondary'
                    }`}
                    onClick={() => setHamburgerOpened(!hamburgerOpened)}
                    aria-label="Toggle menu"
                >
                    <span className='text-2xl'>
                        {hamburgerOpened ? <BiX /> : <BiMenu />}
                    </span>
                </motion.button>
                {hamburgerOpened && showHamburgerMenu()}
            </div>

            {/* Desktop: Navigation Links */}
            <motion.ul
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className='hidden md:flex items-center space-x-1 font-mono text-sm'
            >
                <Link to="/">
                    <li className={`px-4 py-2 rounded-md transition-all ${
                        selectedNav === ''
                            ? (isDark
                                ? 'text-dark-brand-primary bg-dark-bg-secondary border border-dark-border'
                                : 'text-light-brand-primary bg-light-bg-secondary border border-light-border')
                            : (isDark
                                ? 'text-dark-text-primary hover:bg-dark-bg-secondary hover:text-dark-brand-primary'
                                : 'text-light-text-primary hover:bg-light-bg-secondary hover:text-light-brand-primary')
                    }`}>
                        /home
                    </li>
                </Link>
                <Link to="/about">
                    <li className={`px-4 py-2 rounded-md transition-all ${
                        selectedNav === 'about'
                            ? (isDark
                                ? 'text-dark-brand-primary bg-dark-bg-secondary border border-dark-border'
                                : 'text-light-brand-primary bg-light-bg-secondary border border-light-border')
                            : (isDark
                                ? 'text-dark-text-primary hover:bg-dark-bg-secondary hover:text-dark-brand-primary'
                                : 'text-light-text-primary hover:bg-light-bg-secondary hover:text-light-brand-primary')
                    }`}>
                        /about
                    </li>
                </Link>
                <Link to={{pathname: "/projects", state: { tab: "All"}}}>
                    <li className={`px-4 py-2 rounded-md transition-all ${
                        selectedNav === 'projects'
                            ? (isDark
                                ? 'text-dark-brand-primary bg-dark-bg-secondary border border-dark-border'
                                : 'text-light-brand-primary bg-light-bg-secondary border border-light-border')
                            : (isDark
                                ? 'text-dark-text-primary hover:bg-dark-bg-secondary hover:text-dark-brand-primary'
                                : 'text-light-text-primary hover:bg-light-bg-secondary hover:text-light-brand-primary')
                    }`}>
                        /projects
                    </li>
                </Link>
                <Link to="/hireme">
                    <li className={`px-4 py-2 rounded-md transition-all ${
                        selectedNav === 'hireme'
                            ? (isDark
                                ? 'text-dark-brand-primary bg-dark-bg-secondary border border-dark-border'
                                : 'text-light-brand-primary bg-light-bg-secondary border border-light-border')
                            : (isDark
                                ? 'text-dark-text-primary hover:bg-dark-bg-secondary hover:text-dark-brand-primary'
                                : 'text-light-text-primary hover:bg-light-bg-secondary hover:text-light-brand-primary')
                    }`}>
                        /contact
                    </li>
                </Link>
            </motion.ul>

            {/* Desktop: Social Icons + Theme Toggle */}
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className='hidden md:flex items-center gap-4'
            >
                <SocialIcons />
                {/* <ThemeToggle /> */}
            </motion.div>
        </nav>
    )
}

export default Navbar
